import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import SectionHead from '../ui/SectionHead';
import Reveal from '../ui/Reveal';
import { about, profile } from '../../data/profile';
import useDubaiTime from '../../hooks/useDubaiTime';
import desk from '../../assets/images/exp1.jpg';
import keyboard from '../../assets/images/exp2.jpg';
import './About.css';

export default function About() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const time = useDubaiTime();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y1 = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const y2 = useTransform(scrollYProgress, [0, 1], [120, -120]);
  const rot = useTransform(scrollYProgress, [0, 1], [-4, 2]);

  return (
    <section className="section about" id="about" ref={ref}>
      <div className="container">
        <SectionHead
          index="04"
          label="About"
          title={[{ text: 'The model is the' }, { text: 'easy part.', em: true }]}
        />

        <div className="about__grid">
          <div className="about__visual">
            <motion.figure
              className="about__img about__img--wide"
              style={{ y: reduce ? 0 : y1 }}
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            >
              <img src={desk} alt="Ayaan at his desk, reviewing a recommendation service" loading="lazy" />
              <figcaption className="mono">Kalsym, Subang Jaya &middot; 2024</figcaption>
            </motion.figure>
            <motion.figure
              className="about__img about__img--tall"
              style={{ y: reduce ? 0 : y2, rotate: reduce ? 0 : rot }}
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            >
              <img src={keyboard} alt="Desk setup with a mechanical keyboard and a NestJS project open" loading="lazy" />
              <figcaption className="mono">Document digitization, in progress</figcaption>
            </motion.figure>
            <Reveal className="about__badge card" delay={0.3}>
              <span className="mono">Based in</span>
              <span className="about__badge-city">{profile.location}</span>
              <span className="mono about__badge-time">
                <span className="dot-live" /> {time} GST
              </span>
            </Reveal>
          </div>

          <div className="about__copy">
            <Reveal as="p" className="about__intro">
              {about.intro}
            </Reveal>
            {about.paragraphs.map((p, i) => (
              <Reveal as="p" className="body about__p" key={p.slice(0, 20)} delay={0.08 * (i + 1)}>
                {p}
              </Reveal>
            ))}

            <Reveal className="about__principles" delay={0.2}>
              {about.principles.map((pr, i) => (
                <div className="about__principle" key={pr.k}>
                  <span className="mono accent">0{i + 1}</span>
                  <div>
                    <h4 className="about__principle-k">{pr.k}</h4>
                    <p className="small">{pr.v}</p>
                  </div>
                </div>
              ))}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
